import { Suspense, useEffect, useMemo, type ComponentType, type ReactNode } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Home, LogOut, ShieldCheck, UserRound } from "lucide-react";
import { from, roleLabel, roleRank, unwrap, useSession, type ModuleMenuItem, type ModuleUi } from "@vixio/data";
import { AppShell, Button, Callout, EmptyState, Menu, OrgSwitcher, PageContextProvider, PageSkeleton, Panel, ThemeMenu, type NavGroup } from "@vixio/ui";
import { usePublicContacts } from "@vixio/core-ui";
import { useAuth } from "../auth/AuthProvider";
import { env } from "../env";
import { ProfilePage } from "../pages/ProfilePage";
import { NotFound, Forbidden } from "../pages/Status";
import { ScreenBoundary } from "../shell/ScreenBoundary";

/**
 * Grupos do menu do portal pela ordem que cada módulo dá ao item (docs/UI_MODULOS.md):
 * até 99 "Acompanhar", 100 a 199 "Contratos e financeiro", 200 a 299 "Documentos e cursos", 300 em diante "Sua conta".
 */
const PORTAL_GROUPS: Array<{ key: string; label: string; min: number; max: number }> = [
  { key: "follow", label: "Acompanhar", min: -Infinity, max: 99 },
  { key: "money", label: "Contratos e financeiro", min: 100, max: 199 },
  { key: "docs", label: "Documentos e cursos", min: 200, max: 299 },
  { key: "account", label: "Sua conta", min: 300, max: Infinity },
];

/** Rotas do próprio app dentro do portal (conta de acesso), além das que os módulos trazem. */
const APP_ITEMS: Array<Omit<ModuleMenuItem, "domain">> = [
  { path: "/portal/seguranca", label: "Senha e segurança", icon: ShieldCheck, order: 310, minRole: "client" },
];

/** Portal do cliente (/portal): layout próprio, papel client (e acima). */
export function PortalLayout({ mods }: { mods: ModuleUi[] }) {
  const session = useSession();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const rank = session.org?.rank ?? 0;
  const items = useMemo(
    () =>
      [
        ...mods.flatMap((m) => m.portal?.menu ?? []).filter((i) => rank >= roleRank(i.minRole)),
        ...APP_ITEMS,
      ].sort((a, b) => (a.order ?? 250) - (b.order ?? 250) || a.label.localeCompare(b.label, "pt-BR")),
    [mods, rank],
  );
  const routes = mods.flatMap((m) => m.portal?.routes ?? []);
  const location = useLocation();
  const page = useMemo(() => {
    const p = location.pathname;
    const hit = items.filter((i) => p === i.path || p.startsWith(`${i.path}/`)).sort((a, b) => b.path.length - a.path.length)[0];
    const o = hit?.order ?? 250;
    return {
      section: hit ? PORTAL_GROUPS.find((g) => o >= g.min && o <= g.max)?.label : undefined,
      label: p === "/portal" ? "Início" : hit?.label,
    };
  }, [items, location.pathname]);
  // Título da aba: a tela, a empresa e a área do cliente.
  const orgName = session.org?.name;
  useEffect(() => {
    document.title = [page.label, orgName, "Área do cliente"].filter(Boolean).join(" · ");
    return () => {
      document.title = "Vixio OS";
    };
  }, [page.label, orgName]);
  const homes = mods
    .flatMap((m) => (m.portal?.home ? [{ key: m.key, Home: m.portal.home, order: m.portal.homeOrder ?? 50 }] : []))
    .sort((a, b) => a.order - b.order);
  const gates = mods.flatMap((m) => (m.portal?.gate ? [m.portal.gate] : []));
  const topbars = mods.flatMap((m) => (m.portal?.topbar ? [{ key: m.key, Control: m.portal.topbar }] : []));
  // Cada módulo pode exigir algo antes do conteúdo (ex.: aceite dos termos de uso); os portões se aninham.
  const gated = (content: ReactNode) => gates.reduceRight<ReactNode>((inner, Gate) => <Gate>{inner}</Gate>, content);

  const groups = useMemo<NavGroup[]>(
    () => [
      { key: "home", label: "", items: [{ to: "/portal", label: "Início", icon: Home, end: true }] },
      ...PORTAL_GROUPS.map((g) => ({
        key: g.key,
        label: g.label,
        items: items
          .filter((i) => (i.order ?? 250) >= g.min && (i.order ?? 250) <= g.max)
          .map((i) => ({ to: i.path, label: i.label, icon: i.icon, end: i.end })),
      })),
    ],
    [items],
  );

  return (
    <AppShell
      groups={groups}
      brandTo="/portal"
      brandLabel="Área do cliente"
      sidebarFooter={
        <>
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={session.email ?? ""}>
            {session.email}
          </div>
          {env.envLabel ? <div className="mono">ambiente: {env.envLabel}</div> : null}
        </>
      }
      topbar={
        <>
          {session.orgs.length > 1 ? (
            <OrgSwitcher
              orgs={session.orgs.map((o) => ({ id: o.id, name: o.name, roleLabel: roleLabel(o.role) }))}
              currentId={session.org?.id ?? null}
              onSwitch={(id) => {
                session.switchOrg(id);
                navigate("/portal");
              }}
            />
          ) : (
            <span className="text-2" style={{ fontSize: "var(--fs-13)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {session.org?.name}
            </span>
          )}
          <span className="vx-topbar__spacer" />
          {topbars.map(({ key, Control }) => (
            <Control key={key} />
          ))}
          <ThemeMenu />
          <Menu
            label={session.email ?? "Conta"}
            trigger={<Button variant="ghost" iconOnly label="Sua conta" icon={<UserRound />} />}
            items={[
              ...items
                .filter((i) => (i.order ?? 0) >= 300)
                .map((i) => ({ label: i.label, onSelect: () => navigate(i.path) })),
              {
                label: "Sair",
                icon: <LogOut aria-hidden="true" />,
                separatorBefore: true,
                onSelect: () => {
                  void signOut().then(() => navigate("/portal/entrar"));
                },
              },
            ]}
          />
        </>
      }
    >
      <div className="vx-content vx-portal">
        <PageContextProvider value={page}>
          <ScreenBoundary>
            <Suspense fallback={<PageSkeleton bare />}>
              <Routes>
                {/* Senha e 2FA ficam fora dos portões: dá para cuidar da conta mesmo antes de o acesso ser liberado. */}
                <Route path="seguranca" element={<ProfilePage variant="portal" />} />
                <Route
                  path="*"
                  element={gated(
                    <Routes>
                      <Route index element={<PortalHome hasModules={items.length > APP_ITEMS.length} homes={homes} />} />
                      {routes.map((r) => (
                        <Route
                          key={r.path}
                          path={r.path.replace(/^\/portal\/?/, "")}
                          element={rank >= roleRank(r.minRole) ? r.element : <Forbidden />}
                        />
                      ))}
                      <Route path="*" element={<NotFound />} />
                    </Routes>,
                  )}
                />
              </Routes>
            </Suspense>
          </ScreenBoundary>
        </PageContextProvider>
      </div>
    </AppShell>
  );
}

function greeting(now = new Date()): string {
  const h = Number(new Intl.DateTimeFormat("pt-BR", { hour: "numeric", hour12: false, timeZone: "America/Bahia" }).format(now));
  return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
}

function PortalHome({ hasModules, homes }: { hasModules: boolean; homes: Array<{ key: string; Home: ComponentType }> }) {
  const session = useSession();
  const contacts = usePublicContacts(session.org?.id ?? null).data;
  const me = useQuery({
    queryKey: ["core", "people", "me", session.org!.id, session.userId],
    queryFn: () =>
      unwrap<Array<{ full_name: string }>>(
        from("core", "people").select("full_name").eq("org_id", session.org!.id).eq("user_id", session.userId).limit(1),
      ),
  });
  const first = me.data?.[0]?.full_name.split(" ")[0];
  return (
    <>
      <div className="vx-hello">
        <div>
          <h1 className="vx-hello__title">{first ? `${greeting()}, ${first}` : greeting()}</h1>
          <p className="vx-hello__sub">Tudo o que você tem com a {session.org!.name}, num lugar só.</p>
        </div>
      </div>
      {homes.map(({ key, Home: HomePanel }) => (
        <div key={key} className="vx-block">
          <HomePanel />
        </div>
      ))}
      {hasModules || homes.length > 0 ? null : (
        <Panel>
          <EmptyState title="Sua área está sendo preparada">
            Projetos, faturas, entregas para aprovar e chamados aparecem aqui assim que a equipe liberar o portal para você.
          </EmptyState>
        </Panel>
      )}
      <div className="vx-block">
        <Callout tone="info">
          Precisa de ajuda? Abra um chamado
          {contacts?.contact_email ? <> ou escreva para <a href={`mailto:${contacts.contact_email}`}>{contacts.contact_email}</a></> : null}.
        </Callout>
      </div>
    </>
  );
}
